import type { ReactNode } from "react"
import { GripVertical } from "lucide-react"

interface Props {
  title: string
  children: ReactNode
  badge?: ReactNode
  count?: number | string
  helpText?: string
  live?: boolean
  /** True when the panel's data is synthetic demo content — renders a
      SIMULATED badge so fabricated data is never mistaken for a live feed. */
  demo?: boolean
  footer?: ReactNode
  className?: string
  titleAccessory?: ReactNode
}

export function Panel({
  title,
  children,
  badge,
  count,
  helpText,
  live,
  demo,
  footer,
  className = "",
  titleAccessory,
}: Props) {
  return (
    <div className={`ui-panel panel-mount flex flex-col min-h-0 overflow-hidden ${className}`}>
      <header className="panel-drag-handle flex items-center gap-2 px-3 py-2 border-b border-[color:var(--border)] cursor-move select-none">
        <h3 className="ui-header-title truncate">{title}</h3>
        {helpText && (
          <span
            className="text-[10px] text-[color:var(--text-dim)] cursor-help leading-none"
            title={helpText}
            aria-label="help"
          >
            ⓘ
          </span>
        )}
        <div className="flex-1" />
        {titleAccessory}
        {demo && (
          <span
            className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 uppercase tracking-wider border border-amber-500/30"
            title="Synthetic demo data — not a live feed"
          >
            simulated
          </span>
        )}
        {live && !demo && <span className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400 uppercase tracking-wider"><span className="w-1.5 h-1.5 bg-emerald-400 rounded-full inline-block" />live</span>}
        {count != null && (
          <span className="text-[10px] text-[color:var(--text-dim)] tabular-nums">
            {typeof count === "number" ? count.toLocaleString() : count}
          </span>
        )}
        {badge}
        <span className="panel-grip" title="Drag the header to move this panel"><GripVertical size={14} aria-hidden="true" /></span>
      </header>
      <div className="flex-1 min-h-0 overflow-y-auto">
        {children}
      </div>
      {footer && (
        <footer className="px-3 py-1.5 text-[10px] text-[color:var(--text-dim)] border-t border-[color:var(--border)]">
          {footer}
        </footer>
      )}
    </div>
  )
}

export function severityClasses(sev: string): { bg: string; text: string } {
  switch (sev) {
    case "critical":
      return { bg: "bg-red-500/15", text: "text-red-400" }
    case "high":
    case "warn":
      return { bg: "bg-orange-500/15", text: "text-orange-400" }
    case "medium":
      return { bg: "bg-amber-500/15", text: "text-amber-400" }
    default:
      return { bg: "bg-sky-500/15", text: "text-sky-400" }
  }
}

export function trendGlyph(trend: string): string {
  if (trend === "up") return "↑"
  if (trend === "down") return "↓"
  return "→"
}

export function trendClass(trend: string): string {
  if (trend === "up") return "text-red-400"
  if (trend === "down") return "text-emerald-400"
  return "text-[color:var(--text-muted)]"
}
